import { useState } from 'react';
import { Link, useParams } from 'react-router';
import { ArrowLeft } from 'lucide-react';
import { Badge } from '@repo/ui/src/components/ui/badge';
import { Button } from '@repo/ui/src/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@repo/ui/src/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@repo/ui/src/components/ui/table';
import type { AdminPromotionRedemption } from '@repo/shared-types';
import { usePromotion } from '../hooks/usePromotions';
import { PromotionStatusBadge } from '../components/PromotionStatusBadge';
import { formatVnDateTime, formatVnd } from '../lib/adminFormat';
import { PromotionSheet } from './components/PromotionSheet';

const CYCLE_LABEL: Record<'MONTHLY' | 'ANNUAL', string> = {
  MONTHLY: 'Theo tháng',
  ANNUAL: 'Theo năm',
};

const REDEMPTION_STATUS_LABEL: Record<AdminPromotionRedemption['status'], string> = {
  PENDING: 'Chờ thanh toán',
  PAID: 'Đã thanh toán',
  EXPIRED: 'Hết hạn',
};

export function PromotionDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { data: promotion, isLoading, error } = usePromotion(id);
  const [editOpen, setEditOpen] = useState(false);

  if (isLoading) {
    return <p className="text-muted-foreground">Đang tải...</p>;
  }

  if (error || !promotion) {
    return (
      <div className="flex flex-col gap-4">
        <Link to="/promotions" className="inline-flex items-center gap-1 text-sm text-surface-600 hover:underline">
          <ArrowLeft className="h-4 w-4" /> Quay lại danh sách ưu đãi
        </Link>
        <p className="text-destructive">Không thể tải ưu đãi này. Vui lòng thử lại sau.</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <Link to="/promotions" className="inline-flex items-center gap-1 text-sm text-surface-600 hover:underline w-fit">
        <ArrowLeft className="h-4 w-4" /> Quay lại danh sách ưu đãi
      </Link>

      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="font-display text-2xl font-semibold tracking-tight text-surface-900">
              {promotion.name}
            </h1>
            <PromotionStatusBadge active={promotion.active} startsAt={promotion.startsAt} endsAt={promotion.endsAt} />
          </div>
          <p className="text-sm text-muted-foreground mt-1">
            {promotion.code ?? 'Chiến dịch tự áp dụng, không cần mã'}
          </p>
          {promotion.headline && (
            <p className="text-sm text-surface-700 mt-2 max-w-lg">{promotion.headline}</p>
          )}
        </div>
        <Button type="button" onClick={() => setEditOpen(true)}>
          Sửa
        </Button>
      </div>

      <PromotionSheet mode="edit" promotion={promotion} open={editOpen} onOpenChange={setEditOpen} />

      <Card>
        <CardHeader>
          <CardTitle className="font-display font-semibold tracking-tight text-surface-900">
            Thông tin ưu đãi
          </CardTitle>
        </CardHeader>
        <CardContent>
          <dl className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm sm:grid-cols-4">
            <div>
              <dt className="text-muted-foreground">Giảm</dt>
              <dd className="mt-0.5 tabular-nums">{promotion.percentOff}%</dd>
            </div>
            <div>
              <dt className="text-muted-foreground">Lượt dùng</dt>
              <dd className="mt-0.5 tabular-nums">
                {promotion.maxRedemptions != null
                  ? `${promotion.redemptionCount}/${promotion.maxRedemptions}`
                  : promotion.redemptionCount}
              </dd>
            </div>
            <div>
              <dt className="text-muted-foreground">Bắt đầu</dt>
              <dd className="mt-0.5 tabular-nums">{formatVnDateTime(promotion.startsAt)}</dd>
            </div>
            <div>
              <dt className="text-muted-foreground">Kết thúc</dt>
              <dd className="mt-0.5 tabular-nums">{formatVnDateTime(promotion.endsAt)}</dd>
            </div>
            <div>
              <dt className="text-muted-foreground">Áp dụng cho gói</dt>
              <dd className="mt-0.5 flex flex-wrap gap-1">
                {promotion.planCodes.map((code) => (
                  <Badge key={code} variant="muted">
                    {code}
                  </Badge>
                ))}
              </dd>
            </div>
            <div>
              <dt className="text-muted-foreground">Áp dụng cho chu kỳ</dt>
              <dd className="mt-0.5">{promotion.cycles.map((c) => CYCLE_LABEL[c]).join(', ')}</dd>
            </div>
            <div>
              <dt className="text-muted-foreground">Chủ sở hữu</dt>
              <dd className="mt-0.5">{promotion.ownerLabel ?? '—'}</dd>
            </div>
            <div>
              <dt className="text-muted-foreground">Tạo lúc</dt>
              <dd className="mt-0.5 tabular-nums">{formatVnDateTime(promotion.createdAt)}</dd>
            </div>
            <div>
              <dt className="text-muted-foreground">Cập nhật lúc</dt>
              <dd className="mt-0.5 tabular-nums">{formatVnDateTime(promotion.updatedAt)}</dd>
            </div>
            <div className="col-span-2 sm:col-span-4">
              <dt className="text-muted-foreground">Ghi chú nội bộ</dt>
              <dd className="mt-0.5 whitespace-pre-wrap">{promotion.note ?? '—'}</dd>
            </div>
          </dl>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="font-display font-semibold tracking-tight text-surface-900">Lượt dùng</CardTitle>
          <p className="text-sm text-muted-foreground">200 lượt gần nhất</p>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Tổ chức</TableHead>
                <TableHead>Trạng thái hoá đơn</TableHead>
                <TableHead className="text-right">Giá niêm yết</TableHead>
                <TableHead className="text-right">Giảm</TableHead>
                <TableHead className="text-right">Đã trả</TableHead>
                <TableHead>Tạo lúc</TableHead>
                <TableHead>Thanh toán lúc</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {promotion.redemptions.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="h-24 text-center text-muted-foreground">
                    Chưa có lượt dùng nào.
                  </TableCell>
                </TableRow>
              ) : (
                promotion.redemptions.map((r) => (
                  <TableRow key={r.invoiceId}>
                    <TableCell>{r.organizationName}</TableCell>
                    <TableCell>
                      <Badge variant={r.status === 'PAID' ? 'success' : r.status === 'EXPIRED' ? 'muted' : 'secondary'}>
                        {REDEMPTION_STATUS_LABEL[r.status]}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right tabular-nums">{formatVnd(r.listAmountVnd)}</TableCell>
                    <TableCell className="text-right tabular-nums">{formatVnd(r.discountVnd)}</TableCell>
                    <TableCell className="text-right tabular-nums">{formatVnd(r.amountVnd)}</TableCell>
                    <TableCell className="whitespace-nowrap tabular-nums text-surface-600">
                      {formatVnDateTime(r.createdAt)}
                    </TableCell>
                    <TableCell className="whitespace-nowrap tabular-nums text-surface-600">
                      {r.paidAt ? formatVnDateTime(r.paidAt) : '—'}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
